/**
 * Error envelope of the alert/channel APIs (api.md §3): {code, message, data, details[]}.
 * The generated client types it as RpcStatus, which is wrong — read errors through here.
 */

/** Mirrors apierr.EnvelopeRouteRoots in lego app/tracking. */
export const ENVELOPE_ROUTE_ROOTS = ['/api/v1/alerts', '/api/v1/alert-instances', '/api/v1/channels'];

export interface EnvelopeDetail {
  /** camelCase; empty for a form-level error. */
  field: string;
  reason: string;
  meta?: Record<string, any>;
}

export interface ApiEnvelopeError {
  /** null when no response arrived. */
  status: number | null;
  message: string;
  details: EnvelopeDetail[];
}

/** Root match on a segment boundary; the URL may carry host, base path and query. */
export const isEnvelopeRoute = (url: string): boolean => {
  const path = url.split(/[?#]/)[0];
  return ENVELOPE_ROUTE_ROOTS.some((root) => path.endsWith(root) || path.includes(`${root}/`));
};

export const parseEnvelopeError = (err: any): ApiEnvelopeError => {
  const response = err && err.response;
  if (!response) {
    return { status: null, message: '', details: [] };
  }
  const data = response.data || {};
  return {
    status: response.status,
    message: typeof data.message === 'string' ? data.message : '',
    details: Array.isArray(data.details) ? data.details : [],
  };
};

/** First reason reported for each field, keyed by field name. */
export const fieldReasons = (details: EnvelopeDetail[]): Record<string, string> =>
  details.reduce<Record<string, string>>((acc, d) => {
    if (d.field && !(d.field in acc)) {
      acc[d.field] = d.reason;
    }
    return acc;
  }, {});

/** meta[key] of the first detail that carries it. */
export const detailMeta = <T = any>(
  details: EnvelopeDetail[],
  key: string,
): T | undefined => {
  const hit = details.find((d) => d.meta && d.meta[key] !== undefined);
  return hit ? (hit.meta![key] as T) : undefined;
};
